import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { eng1241CaseInsensitiveParamsEnabled } from "@/flags/ldClientBridge";

export type HitRow = {
  platform?: string;
  ewitness_group_type?: string;
  [key: string]: unknown;
};

export type TrackerState = {
  query: Record<string, unknown>;
  totalHits: number;
  platform: string;
  poolType: string;
  poolEntitled: boolean;
  dateRange: Record<string, unknown>;
  hits: HitRow[];
  selectedHits: Record<string, unknown>;
  selectedRow: Record<string, unknown>;
  context: Record<string, unknown>;
  visibility: boolean;
  aggregations: Record<string, Record<string, number>>;
  visibleAggregations: Record<string, Record<string, boolean>>;
  loader: boolean;
  contextSearchLoading: boolean;
  updated: boolean;
  selectedTab: string | null;
  sortedColumn: { id: string; desc: boolean };
  queryTookMs: number | null;
};

function normalizeBlacklistsForIntent(blacklists: unknown): string {
  if (!blacklists) return "";
  if (Array.isArray(blacklists)) {
    return blacklists.map((b) => String(b)).sort().join(",");
  }
  if (typeof blacklists === "object") {
    try {
      return Object.entries(blacklists as Record<string, unknown>)
        .filter(([, v]) => Boolean(v))
        .map(([k]) => k)
        .sort()
        .join(",");
    } catch {
      return "";
    }
  }
  return String(blacklists);
}

function searchIntentKey(query: Record<string, unknown> | null | undefined): string {
  if (!query) return "";
  const relevant = {
    query: String(query.query ?? "").trim().replace(/\s+/g, " "),
    platform: String(query.platform ?? "All"),
    searchPool: String(query.searchPool ?? "All"),
    from: Number(query.from) || 0,
    size: Number(query.size) || 0,
    field: query.field ?? null,
    order: query.order ?? null,
    startDate: String(query.startDate ?? ""),
    endDate: String(query.endDate ?? ""),
    blacklists: normalizeBlacklistsForIntent(query.blacklists),
  };
  try {
    return JSON.stringify(relevant);
  } catch {
    return "";
  }
}

const initialState: TrackerState = {
  query: { query: "" },
  totalHits: 0,
  platform: "All",
  poolType: "All",
  /**
   * Matches CRA `ew-ui/src/reducers/SearchReducer.js`: starts entitled and is not downgraded
   * by group state. Real plan/entitlement gating happens at the API + scraper-modal level.
   */
  poolEntitled: true,
  dateRange: {},
  hits: [],
  selectedHits: {},
  selectedRow: {},
  context: {},
  visibility: true,
  aggregations: { Platform: {}, Group: {} },
  visibleAggregations: { Group: {} },
  loader: true,
  contextSearchLoading: false,
  updated: false,
  selectedTab: null,
  sortedColumn: { id: "_source.timestamp", desc: true },
  queryTookMs: null,
};

export const searchHitsSlice = createSlice({
  name: "hitsTracker",
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<Record<string, unknown>>) => {
      const next = action.payload;
      const prev = state.query;
      const eng1241Enabled = eng1241CaseInsensitiveParamsEnabled();
      if (eng1241Enabled) {
        const sameIntent =
          Boolean(prev && Object.keys(prev).length > 0) &&
          searchIntentKey(prev) === searchIntentKey(next);
        if (sameIntent) {
          return;
        }
      }
      state.query = next;
      let changed = true;
      try {
        changed = JSON.stringify(prev) !== JSON.stringify(next);
      } catch {
        changed = true;
      }
      if (changed) {
        state.updated = false;
        const isPagingOnlyChange =
          Boolean(prev && Object.keys(prev).length > 0) &&
          prev.query === next.query &&
          prev.platform === next.platform &&
          prev.searchPool === next.searchPool &&
          (prev.startDate ?? null) === (next.startDate ?? null) &&
          (prev.endDate ?? null) === (next.endDate ?? null) &&
          prev.field === next.field &&
          prev.order === next.order &&
          (prev.from !== next.from || prev.size !== next.size);
        if (eng1241Enabled) {
          if (!isPagingOnlyChange && !state.contextSearchLoading) {
            if (
              state.context?.id != null &&
              String(state.context.id).trim() !== ""
            ) {
              state.context = {};
            }
            state.totalHits = 0;
            state.queryTookMs = null;
            state.hits = [];
          }
        } else if (!isPagingOnlyChange) {
          state.totalHits = 0;
          state.queryTookMs = null;
          state.hits = [];
        }
      }
    },
    setContextSearchDisplayQuery: (state, action: PayloadAction<string>) => {
      state.query = { ...state.query, query: action.payload };
    },
    setContext: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.context = action.payload;
    },
    setVisibility: (state, action: PayloadAction<boolean>) => {
      state.visibility = action.payload;
    },
    setPlatform: (state, action: PayloadAction<string>) => {
      state.platform = action.payload;
    },
    setPoolType: (state, action: PayloadAction<string>) => {
      state.poolType = action.payload;
    },
    setPoolEntitled: (state, action: PayloadAction<boolean>) => {
      state.poolEntitled = action.payload;
    },
    setDateRange: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.dateRange = action.payload;
    },
    updateTotalHits: (
      state,
      action: PayloadAction<{ total?: number; type?: string }>,
    ) => {
      if (action.payload.type === "context") {
        state.totalHits = state.hits.length;
      } else {
        state.totalHits = action.payload.total ?? 0;
      }
      const platforms: Record<string, number> = {};
      for (const row of state.hits) {
        const p = row.platform ?? "unknown";
        platforms[p] = (platforms[p] ?? 0) + 1;
      }
      state.aggregations.Platform = platforms;
      const groups: Record<string, number> = {};
      const enabledGroups: Record<string, boolean> = {};
      for (const row of state.hits) {
        const groupType = row.ewitness_group_type ?? "unknown";
        groups[groupType] = (groups[groupType] ?? 0) + 1;
        enabledGroups[groupType] = true;
      }
      state.aggregations.Group = Object.keys(groups)
        .sort()
        .reduce<Record<string, number>>((obj, key) => {
          obj[key] = groups[key];
          return obj;
        }, {});
      state.visibleAggregations.Group = enabledGroups;
    },
    updateHits: (state, action: PayloadAction<HitRow[]>) => {
      state.hits = action.payload;
    },
    updateQueryTook: (state, action: PayloadAction<number | null>) => {
      state.queryTookMs = action.payload;
    },
    updateRowSelect: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.selectedRow = action.payload;
    },
    updateRows: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.selectedHits = action.payload;
    },
    updateVisibleAggregations: (
      state,
      action: PayloadAction<Record<string, Record<string, boolean>>>,
    ) => {
      state.visibleAggregations = action.payload;
    },
    updateTab: (state, action: PayloadAction<{ tab?: string } | string | null>) => {
      if (typeof action.payload === "object" && action.payload !== null) {
        state.selectedTab = action.payload.tab ?? null;
      } else {
        state.selectedTab = action.payload as string | null;
      }
    },
    showLoader: (state, action: PayloadAction<boolean>) => {
      state.loader = action.payload;
    },
    setContextSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.contextSearchLoading = action.payload;
    },
    registerPageUpdate: (state, action: PayloadAction<boolean>) => {
      state.updated = action.payload;
    },
    updateSortedColumn: (
      state,
      action: PayloadAction<{ id: string; desc: boolean }>,
    ) => {
      state.sortedColumn = action.payload;
    },
  },
});

export const {
  setQuery,
  setContextSearchDisplayQuery,
  setPlatform,
  setPoolType,
  setPoolEntitled,
  setDateRange,
  setContext,
  setVisibility,
  updateHits,
  updateQueryTook,
  updateTotalHits,
  updateRowSelect,
  updateRows,
  showLoader,
  setContextSearchLoading,
  registerPageUpdate,
  updateSortedColumn,
  updateTab,
  updateVisibleAggregations,
} = searchHitsSlice.actions;

export default searchHitsSlice.reducer;
